/**
 * Agent workflows list (admin). Route: /agent-workflow
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { hubService, type AgentWorkflow } from '../services/hub.service';

const DEFAULT_SYSTEM = 'chatbot';

export function AgentWorkflowList() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<AgentWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [system, setSystem] = useState(DEFAULT_SYSTEM);
  const [botId, setBotId] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [publishingId, setPublishingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listAgentWorkflows(
        {
          system: system.trim() || undefined,
          botId: botId.trim() || undefined,
        },
        token,
      );
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [getToken, system, botId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((w) => w.name.toLowerCase().includes(q));
  }, [items, nameFilter]);

  const handlePublish = async (id: string) => {
    setPublishingId(id);
    setError(null);
    try {
      const token = getToken();
      await hubService.publishAgentWorkflow(id, {}, token);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Agent workflows</h1>
            <p className="text-gray-600 mt-1 text-sm">
              Design and publish multi-step agent graphs (draft vs published versions).
            </p>
          </div>
          <Link
            to="/agent-workflow/new"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            New workflow
          </Link>
        </div>

        <div className="mb-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">System</label>
            <input
              type="text"
              value={system}
              onChange={(e) => setSystem(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm w-36"
              placeholder="chatbot"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Bot ID</label>
            <input
              type="text"
              value={botId}
              onChange={(e) => setBotId(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm w-40"
              placeholder="optional"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Filter name</label>
            <input
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm w-48"
              placeholder="contains…"
            />
          </div>
          <button
            type="button"
            onClick={() => load()}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            {filtered.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <p>No workflows match. Create one to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">System / Bot</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filtered.map((w) => (
                      <tr key={w.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link
                            to={`/agent-workflow/${w.id}`}
                            className="font-medium text-blue-600 hover:text-blue-800"
                          >
                            {w.name}
                          </Link>
                          {w.description ? (
                            <p className="text-xs text-gray-500 truncate max-w-xs mt-0.5">{w.description}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {w.system}
                          <span className="text-gray-400"> · </span>
                          {w.botId}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          draft v{w.draftVersion}
                          <span className="text-gray-400"> / </span>
                          published v{w.publishedVersion}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              w.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {w.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          <Link
                            to={`/agent-workflow/${w.id}`}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            disabled={publishingId === w.id}
                            onClick={() => handlePublish(w.id)}
                            className="text-sm text-gray-700 hover:text-gray-900 disabled:opacity-50"
                          >
                            {publishingId === w.id ? 'Publishing…' : 'Publish'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
